/**
 * useDaddyTalkHeartbeat — Presence ping for recruiters.
 *
 * V1.6.2. While an eligible recruiter is signed in AND the tab is
 * visible, ping `POST /api/daddytalk/heartbeat` every 30 s. Backend
 * bumps `user_chat_settings.last_seen_at`; candidates then see a
 * TRUE-presence green dot in the availability drawer, not just the
 * self-set `chat_active` flag.
 *
 * Runs silently — errors are swallowed. No render effect.
 */
import { useEffect, useRef } from "react";
import api from "@/lib/api";

const HEARTBEAT_MS = 30_000;
const PRESENCE_ROLES = new Set(["recruiter", "admin", "hiring_manager"]);

export default function useDaddyTalkHeartbeat(user) {
    const timerRef = useRef(null);

    useEffect(() => {
        if (!user || !PRESENCE_ROLES.has(user.role)) return undefined;

        let stopped = false;
        const ping = async () => {
            if (stopped) return;
            if (typeof document !== "undefined" && document.hidden) return;
            try {
                await api.post("/daddytalk/heartbeat");
            } catch {
                // Best-effort: never surface an error for a background
                // presence beacon. A failed heartbeat just means the
                // recruiter's dot falls back to "away" after 90 s.
            }
        };

        // Immediate first ping so the recruiter appears online the
        // moment they open the app — no 30 s wait for the first tick.
        ping();
        timerRef.current = setInterval(ping, HEARTBEAT_MS);

        // Re-ping instantly when the tab regains focus after being
        // hidden — critical for laptops coming back from sleep so
        // recruiters don't look "away" for up to 90 s post-wake.
        const onVis = () => {
            if (!document.hidden) ping();
        };
        document.addEventListener("visibilitychange", onVis);

        return () => {
            stopped = true;
            document.removeEventListener("visibilitychange", onVis);
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [user?.id, user?.role]);
}
